import { Form, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import {
  loginUserRequest,
  isAuthErrorResponse,
  isAuthTokenResponse,
} from '@/api/user';
import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { DASHBOARD, JOBS, LOGIN } from '@/constants/routes';
import { useApiMutation } from '@/hooks/api';
import { useUrlState } from '@/hooks/useUrlState';
import { useAppDispatch } from '@/store/hooks';
import { login } from '@/store/userSlice';
import FormErrorAlert from '@/UI/FormErrorAlert';
import { isClientDirectLoginBlocked } from '@/utils/clientDirectLogin';
import { storeAccessToken } from '@/utils/localStorage';
import { buildLoginFormSchema } from '@/utils/validation/formSchemas/authSchemas';

import ClientProfileSelector from './ClientProfileSelector';
import MFAAppSetupInLogin from './MFAAppSetupInLogin';
import MFAMethodSelection from './MFAMethodSelection';
import MFAStep from './MFAStep';
import ResetPasswordPopUp from './ResetPasswordPopUp';

import type {
  AuthErrorResponse,
  AuthTokenResponse,
  MFALoginMethod,
} from '@/api/user';
import type { SchemaSection } from '@/components/form/SchemaForm/types';
import type { AvailableClientProfile } from '@/types/user';
import type { MagicLinkMfaBootstrap } from '@/utils/notificationDeepLink';

/**
 * `mfa_selection` only appears for users with no stored preference; everyone else
 * goes straight to `mfa_verify` with the method the backend already picked.
 */
type LoginScreen =
  'credentials' | 'mfa_selection' | 'mfa_verify' | 'mfa_app_setup';

type ProfileSelectionErrorData = {
  client_profile_id?: string;
  available_profiles?: AvailableClientProfile[];
  message?: string;
};

type LoginErrorPayload = {
  message?: string;
  errors?: {
    errors?: ProfileSelectionErrorData;
    message?: string;
  };
};

type LoginLocationState = {
  magicLinkMfa?: MagicLinkMfaBootstrap;
};

const LoginForm = () => {
  const [resetPass, setResetPass] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [t] = useTranslation();
  const { history } = useUrlState();
  const location = useLocation();
  const [error, setError] = useState('');
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  const [availableProfiles, setAvailableProfiles] = useState<
    AvailableClientProfile[]
  >([]);
  const [loginCredentials, setLoginCredentials] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [screen, setScreen] = useState<LoginScreen>('credentials');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaMethod, setMfaMethod] = useState<MFALoginMethod>('totp');
  const [mfaUserEmail, setMfaUserEmail] = useState('');
  const [postMfaPath, setPostMfaPath] = useState<string | null>(null);
  const [mfaCanPostpone, setMfaCanPostpone] = useState(false);
  const isMountedRef = React.useRef(true);
  const magicLinkMfaBootstrappedRef = React.useRef(false);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (magicLinkMfaBootstrappedRef.current) {
      return;
    }
    const state = location.state as LoginLocationState | null;
    const bootstrap = state?.magicLinkMfa;
    if (!bootstrap?.mfaToken) {
      return;
    }
    magicLinkMfaBootstrappedRef.current = true;
    setMfaToken(bootstrap.mfaToken);
    setMfaUserEmail(bootstrap.userEmail ?? '');
    setPostMfaPath(bootstrap.nextPath);
    if (bootstrap.mfaSelectionRequired) {
      setScreen('mfa_selection');
    } else {
      setMfaMethod(bootstrap.mfaMethod ?? 'totp');
      setScreen('mfa_verify');
    }
    history.replace(LOGIN);
  }, [location.state, history]);

  const formSchema = useMemo(() => buildLoginFormSchema(t), [t]);

  const sections = useMemo<SchemaSection[]>(
    () => [
      {
        key: 'login',
        fields: [
          {
            name: 'email',
            type: 'text',
            label: t('EMAIL'),
            componentProps: { size: 'large' },
          },
          {
            name: 'password',
            type: 'password',
            label: t('PASSWORD_LABEL'),
            componentProps: { size: 'large' },
          },
        ],
      },
    ],
    [t],
  );

  const loginMutation = useApiMutation({
    mutationFn: loginUserRequest,
  });

  const loading = loginMutation.loading;

  const navigateAfterLogin = (nextPath?: string | null) => {
    if (nextPath) {
      history.push(nextPath);
      return;
    }
    if (document.referrer.includes('/jobs')) {
      history.push(JOBS);
    } else {
      history.push(DASHBOARD);
    }
  };

  const processLoginResponse = (
    response: AuthTokenResponse | AuthErrorResponse | undefined,
  ) => {
    if (!response) {
      setError('Login failed');
      return;
    }

    if (
      isAuthErrorResponse(response) &&
      (response.status === 400 ||
        response.status === 401 ||
        response.status === 403)
    ) {
      const errorBody = response.data as LoginErrorPayload | undefined;
      const errorData = errorBody?.errors?.errors;

      if (
        errorData?.client_profile_id === 'Profile selection required' &&
        errorData.available_profiles &&
        Array.isArray(errorData.available_profiles)
      ) {
        setAvailableProfiles(errorData.available_profiles);
        setShowProfileSelector(true);
        return;
      }

      setShowProfileSelector(false);

      if (response.status === 403) {
        setError(t('CLIENT_LOGIN_NOT_ALLOWED'));
        return;
      }

      const msg =
        errorBody?.message || errorBody?.errors?.message || 'Login failed';
      setError(msg);
      return;
    }

    if (!isAuthTokenResponse(response)) {
      setShowProfileSelector(false);
      setError('Login failed');
      return;
    }

    if (response.mfa_selection_required && response.mfa_token) {
      setShowProfileSelector(false);
      setMfaToken(response.mfa_token);
      setMfaUserEmail(response.user_email ?? '');
      setMfaCanPostpone(!!response.can_postpone);
      setScreen('mfa_selection');
      return;
    }

    if (response.mfa_required && response.mfa_token) {
      setShowProfileSelector(false);
      setMfaToken(response.mfa_token);
      setMfaMethod(response.mfa_method ?? 'totp');
      setMfaUserEmail(response.user_email ?? '');
      setScreen('mfa_verify');
      return;
    }

    if (!response.user) {
      setShowProfileSelector(false);
      setError('Login failed');
      return;
    }

    if (isClientDirectLoginBlocked(response.user)) {
      setShowProfileSelector(false);
      setError(t('CLIENT_LOGIN_NOT_ALLOWED'));
      return;
    }

    dispatch(
      login({
        user: response.user,
        access_token: response.access_token,
        client_profile_id: response.client_profile_id,
      }),
    );

    setShowProfileSelector(false);
    setError('');
    navigateAfterLogin();
  };

  const onFinish = async (values: Record<string, unknown>) => {
    if (!isMountedRef.current) {
      return;
    }

    setError('');
    setLoginCredentials(values);
    loginMutation.reset();

    try {
      const response = await loginMutation.mutateAsync(values);

      if (!isMountedRef.current) {
        return;
      }

      processLoginResponse(response);
    } catch {
      if (!isMountedRef.current) {
        return;
      }
    }
  };

  const handleProfileSelect = async (profileId: string) => {
    if (!isMountedRef.current) {
      return;
    }

    if (!loginCredentials) {
      setError('Login failed');
      setShowProfileSelector(false);
      return;
    }

    setError('');
    loginMutation.reset();

    try {
      const response = await loginMutation.mutateAsync({
        ...loginCredentials,
        client_profile_id: profileId,
      });

      if (!isMountedRef.current) {
        return;
      }

      processLoginResponse(response);
    } catch {
      if (!isMountedRef.current) {
        return;
      }
    }
  };

  const handleProfileSelectorCancel = () => {
    setShowProfileSelector(false);
  };

  const resetToLogin = () => {
    setScreen('credentials');
    setMfaToken(null);
    setMfaMethod('totp');
    setMfaUserEmail('');
    setMfaCanPostpone(false);
  };

  const cancelMFA = () => {
    resetToLogin();
    setError('');
  };

  const handleMFASuccess = (loginData: AuthTokenResponse) => {
    if (!isMountedRef.current) return;

    if (isClientDirectLoginBlocked(loginData.user)) {
      resetToLogin();
      setError(t('CLIENT_LOGIN_NOT_ALLOWED'));
      return;
    }

    if (loginData.access_token) {
      storeAccessToken(loginData.access_token);
    }
    dispatch({ type: 'user/loginUser/fulfilled', payload: loginData });
    navigateAfterLogin(postMfaPath);
  };

  if (screen === 'mfa_selection' && mfaToken) {
    return (
      <MFAMethodSelection
        mfaToken={mfaToken}
        userEmail={mfaUserEmail}
        canPostpone={mfaCanPostpone}
        onEmailChosen={(challenge) => {
          setMfaMethod('email');
          setMfaUserEmail(challenge.user_email ?? mfaUserEmail);
          setScreen('mfa_verify');
        }}
        onAppChosen={() => setScreen('mfa_app_setup')}
        onPostponeSuccess={handleMFASuccess}
        onBack={cancelMFA}
      />
    );
  }

  if (screen === 'mfa_app_setup' && mfaToken) {
    return (
      <MFAAppSetupInLogin
        mfaToken={mfaToken}
        onSuccess={handleMFASuccess}
        onBack={() => setScreen('mfa_selection')}
      />
    );
  }

  if (screen === 'mfa_verify' && mfaToken) {
    return (
      <MFAStep
        mfaToken={mfaToken}
        mfaMethod={mfaMethod}
        userEmail={mfaUserEmail}
        onSuccess={handleMFASuccess}
        onBack={cancelMFA}
      />
    );
  }

  return (
    <>
      <FormErrorAlert error={error || loginMutation.error} />
      <SchemaForm
        form={form}
        formSchema={formSchema}
        name="login"
        scrollToFirstError
        onFinish={(values: Record<string, unknown>) => void onFinish(values)}
        sections={sections}
        footer={{
          actionSize: 'large',
          actions: [
            {
              label: t('LOG_IN_TEXT'),
              htmlType: 'submit',
              type: 'primary',
              loading,
            },
          ],
        }}
      >
        <Typography.Link onClick={() => setResetPass(true)}>
          {t('FORGOT_PASSWORD_TEXT')}?
        </Typography.Link>
      </SchemaForm>
      <ResetPasswordPopUp open={resetPass} setOpen={setResetPass} />
      <ClientProfileSelector
        open={showProfileSelector}
        profiles={availableProfiles}
        onSelect={(profileId) => void handleProfileSelect(profileId)}
        onCancel={handleProfileSelectorCancel}
        loading={loading}
      />
    </>
  );
};

export default LoginForm;
